import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as inventoryApi from '../api/inventoryApi';
import { InventoryItem } from '../types/inventory';
import { formatMl } from '../utils/format';

/**
 * Oil name combobox, backed by the live inventory list.
 *
 * WHY THIS EXISTS
 * Deduction matches an oil by its name, EXACTLY. A formula holding "sandalwood"
 * or "Sandalwood " does not match the inventory row "Sandalwood", so its ml
 * never come out of stock — and nothing anywhere reports that. The failure is
 * invisible until someone notices the shelf and the system disagree, by which
 * point there is no way to tell which orders were affected.
 *
 * So: selecting from the dropdown writes the canonical inventory oil_name
 * verbatim, and a name that ISN'T in inventory is called out in red on the spot
 * rather than quietly accepted. Free text is still allowed — a legacy formula
 * with a bad name has to remain visible and editable to be correctable — but
 * the dropdown is the default path and the mismatch is impossible to miss.
 */

// ── useOilInventory ───────────────────────────────────────────────────────────
// The oil list from GET /api/admin/inventory — the same endpoint (and the same
// api client) InventoryPage uses. No new endpoint: the oils, their stock and
// their canonical names are already exactly what that response carries.
//
// Best-effort by design. If inventory can't be reached the picker degrades to a
// plain text input rather than blocking a formula edit; it just cannot vouch for
// the names while it has no list to check them against.
export function useOilInventory(): { oils: InventoryItem[]; loaded: boolean } {
  const [oils, setOils] = useState<InventoryItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    inventoryApi
      .getInventory()
      .then((items) => alive && setOils(items))
      .catch(() => alive && setOils([]))
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  return { oils, loaded };
}

/**
 * name -> manufacturer code, for the oils that have one.
 *
 * Keyed on the EXACT oil_name for the same reason deduction is: a formula
 * holding "sandalwood" is not the inventory row 'Sandalwood', and quietly
 * resolving it here would put a shelf number on a line that will not deduct —
 * hiding the very mismatch isUnknownOil exists to expose.
 */
export function oilCodeMap(oils: InventoryItem[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const o of oils) {
    if (o.manufacturer_code !== null) map.set(o.oil_name, o.manufacturer_code);
  }
  return map;
}

/**
 * The shelf number as an inline tag — "#31" — for wherever an oil is named
 * outside the inventory table itself. Renders nothing at all when the oil has no
 * number or the name doesn't match inventory: a blank is honest, a placeholder
 * dash in a formula row is just noise.
 */
export function OilCodeTag({ code }: { code?: number | null }) {
  if (code == null) return null;
  return (
    <span className="mr-1.5 font-mono text-xs font-semibold text-gold" title={`Shelf number ${code}`}>
      #{code}
    </span>
  );
}

/**
 * True when `name` is a non-empty value that does NOT exactly match an
 * inventory oil_name — i.e. a name that will not deduct.
 *
 * Returns false while the inventory list is still empty/unloaded: an unverified
 * name must not be accused of being wrong.
 */
export function isUnknownOil(name: string, oils: InventoryItem[]): boolean {
  const trimmed = (name ?? '').trim();
  if (!trimmed || oils.length === 0) return false;
  return !oils.some((o) => o.oil_name === trimmed);
}

export default function OilPicker({
  value,
  onChange,
  oils,
  className = '',
  placeholder = 'Oil name',
  disabled = false,
}: {
  value: string;
  onChange: (name: string) => void;
  oils: InventoryItem[];
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Stable id so the input's aria-controls can point at its own listbox — one
  // picker per formula row, so the ids must not collide.
  const listId = useId();

  // Case-insensitive substring match on oil_name. Matching loosely here is the
  // point — the admin types "sandal" and picks the row that writes the exact
  // canonical spelling back.
  const matches = useMemo(() => {
    const q = (value ?? '').trim().toLowerCase();
    if (!q) return oils;
    return oils.filter(
      (o) =>
        o.oil_name.toLowerCase().includes(q) ||
        // The shelf number is on the option, so it has to be typeable too —
        // "#31" and "31" both narrow to the oil numbered 31. Prefixed match, not
        // substring: "3" should not drag in 13, 23 and 30.
        (o.manufacturer_code !== null &&
          String(o.manufacturer_code).startsWith(q.replace(/^#/, '')))
    );
  }, [oils, value]);

  const unknown = isUnknownOil(value, oils);

  // Close on any click outside — the dropdown overlays the rows beneath it, so
  // leaving it open while the admin works elsewhere hides the next row.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Keep the highlighted option in view when arrowing past the visible window.
  useEffect(() => {
    if (!open) return;
    listRef.current?.children[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const choose = (item: InventoryItem) => {
    // The whole point: the canonical name, verbatim, not what was typed.
    onChange(item.oil_name);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlight(0);
        return;
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (matches.length === 0 ? 0 : (h + step + matches.length) % matches.length));
      return;
    }
    if (e.key === 'Enter' && open && matches[highlight]) {
      e.preventDefault();
      choose(matches[highlight]);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        className={`input w-full ${unknown ? '!border-bad' : ''}`}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {/* Existing bad data, surfaced rather than hidden. This row will not
          deduct as it stands, and the admin is the only one who can fix it. */}
      {unknown && (
        <p className="mt-1 text-xs text-bad">not in inventory — will not deduct</p>
      )}

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-edge bg-surface shadow-lg"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs text-sub">
              {oils.length === 0 ? 'Inventory unavailable' : 'No oil matches'}
            </li>
          ) : (
            matches.map((o, i) => {
              // Out of stock stays SELECTABLE: a formula is a recipe, and you
              // may well be writing one for an oil you are about to restock.
              // Greyed so the choice is made knowingly.
              const out = o.quantity_ml <= 0;
              return (
                <li
                  key={o.oil_name}
                  role="option"
                  aria-selected={i === highlight}
                  // onMouseDown, not onClick — mousedown fires before the input's
                  // blur, so the choice lands instead of the list closing first.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(o);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5 text-sm ${
                    i === highlight ? 'bg-edge/60' : ''
                  } ${out ? 'text-sub opacity-60' : ''}`}
                >
                  <span className="truncate">
                    {/* The shelf number, when the oil has one — it is how the
                        bottle is found on the rack, so it belongs next to the
                        name rather than only in the inventory table. */}
                    <OilCodeTag code={o.manufacturer_code} />
                    {o.oil_name}
                  </span>
                  <span className="shrink-0 text-xs text-sub">· {formatMl(o.quantity_ml)}</span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
